/**
 * API utility for making HTTP requests to the backend
 */

// Get API base URL from environment variables
const getApiBaseUrl = () => {
  // Always use REACT_APP_API_URL if it's set
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/+$/, '');
  }
  
  // In development, use the Vite proxy
  if (import.meta.env.DEV) {
    return '/api';
  }
  
  // In production, default to the current origin
  return window.location.origin + '/api';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Makes an API request with proper error handling
 * @param {string} endpoint - The API endpoint (e.g., '/api/auth/civic')
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Object>} - The parsed JSON response
 */
export async function apiRequest(endpoint, options = {}) {
  // Remove any leading/trailing slashes from endpoint for consistency
  let cleanEndpoint = (endpoint || '').replace(/^\/+|\/+$/g, '');

  // If base already ends with '/api' and endpoint starts with 'api', drop the duplicate
  const baseEndsWithApi = /\/api$/i.test(API_BASE_URL);
  if (baseEndsWithApi) {
    cleanEndpoint = cleanEndpoint.replace(/^api\/?/i, '');
  }

  // In production we may also be given absolute '/api/...'; handle it safely
  cleanEndpoint = cleanEndpoint.replace(/^\/+/, '');

  const isFullUrl = /^https?:\/\//i.test(endpoint);

  let url;
  if (isFullUrl) {
    url = endpoint;
  } else {
    url = `${API_BASE_URL}/${cleanEndpoint}`;
  }
  
  // Set default headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Important for cookies/auth
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error || 'API request failed');
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('API request failed:', {
      url,
      error: error.message,
      status: error.status,
    });
    throw error;
  }
}

/**
 * Makes a POST request to the API
 * @param {string} endpoint - The API endpoint
 * @param {Object} body - The request body
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} - The parsed JSON response
 */
export async function post(endpoint, body, options = {}) {
  return apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
    ...options,
  });
}

/**
 * Makes a GET request to the API
 * @param {string} endpoint - The API endpoint
 * @param {Object} params - Query parameters
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} - The parsed JSON response
 */
export async function get(endpoint, params = {}, options = {}) {
  // Build query string safely
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.append(k, v);
  });
  const endpointWithQs = qs.toString()
    ? `${endpoint}${endpoint.includes('?') ? '&' : '?'}${qs.toString()}`
    : endpoint;

  return apiRequest(endpointWithQs, { ...options, method: 'GET' });
}
